import React, { useEffect, useMemo, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({ account_id: "", debit: "", credit: "" });
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);
const chf = (v) => v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function NewJournalEntryDialog({ companyId, open, onOpenChange, onCreated }) {
  const [accounts, setAccounts] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!open || !companyId) return;
    apiClient.get(`/companies/${companyId}/accounts`).then((r) => setAccounts(r.data || [])).catch(() => {});
  }, [open, companyId]);

  const totals = useMemo(() => {
    const d = lines.reduce((s, l) => s + num(l.debit), 0);
    const c = lines.reduce((s, l) => s + num(l.credit), 0);
    return { debit: Math.round(d * 100) / 100, credit: Math.round(c * 100) / 100, diff: Math.round((d - c) * 100) / 100 };
  }, [lines]);

  const updateLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const reset = () => { setDescription(""); setLines([emptyLine(), emptyLine()]); setIdemKey(crypto.randomUUID()); };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        date,
        description,
        lines: lines.map((l) => ({ account_id: l.account_id.trim(), debit: num(l.debit), credit: num(l.credit) })),
        idempotency_key: idemKey,
      };
      const r = await apiClient.post(`/companies/${companyId}/journal-entries`, payload);
      toast.success(`Buchung erfasst (CHF ${chf(totals.debit)})`);
      reset();
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Buchung konnte nicht erfasst werden", { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const accountLabel = (code) => {
    const a = accounts.find((x) => x.code === code);
    return a ? a.name : "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="new-journal-entry-dialog">
        <DialogHeader><DialogTitle>Neue Buchung</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500">Buchungsdatum</label>
            <SwissDateField  value={date} onChange={(e) => setDate(e.target.value)} data-testid="new-entry-date" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Buchungstext</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z.B. Büromaterial Migros" data-testid="new-entry-description" />
          </div>
        </div>
        <div className="mt-2 space-y-2" data-testid="new-entry-lines">
          <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-slate-500 px-1">
            <div className="col-span-6">Konto</div><div className="col-span-3 text-right">Soll</div><div className="col-span-3 text-right">Haben</div>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`new-entry-line-${i}`}>
              <div className="col-span-6 flex items-center gap-2">
                <Input list="journal-accounts-list" value={l.account_id} onChange={(e) => updateLine(i, { account_id: e.target.value })} placeholder="Konto-Nr." className="w-28" data-testid={`new-entry-line-${i}-account`} />
                <span className="text-xs text-slate-500 truncate">{accountLabel(l.account_id.trim())}</span>
              </div>
              <Input type="number" step="0.05" min="0" inputMode="decimal" className="col-span-3 text-right" value={l.debit} onChange={(e) => updateLine(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })} data-testid={`new-entry-line-${i}-debit`} />
              <div className="col-span-3 flex items-center gap-1">
                <Input type="number" step="0.05" min="0" inputMode="decimal" className="text-right" value={l.credit} onChange={(e) => updateLine(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })} data-testid={`new-entry-line-${i}-credit`} />
                <button type="button" className="text-slate-400 hover:text-rose-600 disabled:opacity-30" disabled={lines.length <= 2} onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} data-testid={`new-entry-line-${i}-remove`} title="Zeile entfernen"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          <datalist id="journal-accounts-list">
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
          </datalist>
          <button type="button" onClick={() => setLines((ls) => [...ls, emptyLine()])} className="text-xs text-slate-700 hover:underline inline-flex items-center gap-1" data-testid="new-entry-add-line"><Plus className="h-3.5 w-3.5" /> Zeile hinzufügen (Split-Buchung)</button>
        </div>
        <div className="mt-3 grid grid-cols-12 gap-2 text-sm font-medium border-t border-slate-200 pt-2" data-testid="new-entry-totals">
          <div className="col-span-6 text-slate-600">Total</div>
          <div className="col-span-3 text-right" data-testid="new-entry-total-debit">CHF {chf(totals.debit)}</div>
          <div className="col-span-3 text-right" data-testid="new-entry-total-credit">CHF {chf(totals.credit)}</div>
          <div className={`col-span-12 text-right text-xs ${totals.diff === 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="new-entry-diff">
            Differenz Soll/Haben: CHF {chf(totals.diff)} {totals.diff === 0 ? "✓" : "— Buchung nicht ausgeglichen"}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-entry-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={saving || totals.diff !== 0 || totals.debit <= 0 || description.trim().length < 2} data-testid="new-entry-submit">
            {saving ? "Wird gebucht…" : "Buchen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
